import { useState, createRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import Banner from '../components/Banner';
import Button from '../components/Button';
import Loader from '../components/Loader';
import { Modal, ModalExit, ModalName, ModalPart } from '../components/Modal';
import { useToast } from '../components/Toast';
import { SPACE } from '../utilities/queries';
import { UPDATE_SPACE_BANNER } from '../utilities/mutations';
import { useSpaceContext } from '../contexts/space';

const EditSpaceBannerModal = () => {
  const navigate = useNavigate();
  const params = useParams();
  const input = createRef();
  const toast = useToast();
  const { spaceVars } = useSpaceContext();
  const { loading, data } = useQuery(SPACE, {
    variables: { slug: params.s, ...spaceVars },
  });
  const [updateSpaceBanner] = useMutation(UPDATE_SPACE_BANNER);
  const [isUploading, setIsUploading] = useState(false);

  const close = () =>
    !isUploading && !loading && navigate(`/s/${data.space.slug}/settings/main`);

  return (
    <Modal onClose={() => close()}>
      {loading ? (
        <ModalPart>
          <Loader />
        </ModalPart>
      ) : (
        <>
          <ModalExit disabled={isUploading} />
          <ModalName>Edit space banner</ModalName>
          <Banner
            src={data.space.bannerUrl}
            isAuto={data.space.bannerUrl}
            isModal={!data.space.bannerUrl}
            alt={`${data.space.name} banner image`}
          />
          <ModalPart>
            {data.space.bannerUrl && (
              <Button
                disabled={isUploading}
                onClick={async () => {
                  setIsUploading(true);
                  try {
                    await updateSpaceBanner({
                      variables: {
                        id: data.space.id,
                        banner: null,
                      },
                    });
                    toast({ children: 'Space banner removed' });
                  } catch (error) {
                    toast({ children: error.message });

                    return;
                  }
                  setIsUploading(false);

                  close();
                }}
                isExtended
              >
                Remove
              </Button>
            )}
            <Button
              data-autofocus
              disabled={isUploading}
              onClick={() => input.current.click()}
              isExtended
            >
              Upload
            </Button>
            <input
              ref={input}
              type="file"
              style={{ display: 'none' }}
              accept=".png, .jpg, .jpeg"
              onChange={(e) => {
                const file = e.currentTarget.files[0];
                const reader = new FileReader();

                reader.onload = async function (event) {
                  try {
                    setIsUploading(true);
                    await updateSpaceBanner({
                      variables: {
                        id: data.space.id,
                        banner: event.target.result,
                      },
                    });
                    setIsUploading(false);
                    toast({ children: 'Space banner uploaded' });
                    close();
                  } catch (error) {
                    setIsUploading(false);
                    toast({ children: error.message });

                    return;
                  }
                };
                reader.readAsDataURL(file);
              }}
            />
          </ModalPart>
        </>
      )}
    </Modal>
  );
};

export default EditSpaceBannerModal;
